import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IoBook } from 'react-icons/io5';
import { BrowseCard } from './BrowseCard';
import { meaningScopeFilterHref } from './facets';
import { MEANING_SCOPE_IN, MEANING_SCOPE_OTHER } from '../filters/definitions/meaningFilter';
import { fetchMeaningsIndex, type MeaningsIndex } from '../services/meaningsIndexApi';
import { languageNameForCode, toLanguageCode } from '../utils/languages';
import type { RootState } from '../store/store';

/**
 * The two meanings entries on /browse, mirroring the app's
 * (jnana-native/src/bmScreens/browse/root/index.tsx:285-286): bhajans whose
 * meaning is written in the language you are reading in, and bhajans whose
 * meaning exists only in some other language.
 *
 * Its own component rather than more markup inside Browse for one blunt reason:
 * these are the only tiles on the page whose existence depends on fetched data,
 * and Browse is re-required under a reset module registry by the tenant tests —
 * hooks there mean two copies of React and an invalid-hook-call crash.
 *
 * A COUNT decides whether each tile renders, not a tenant flag. Six of the ten
 * languages have no meanings at all (measured on prod 2026-07-31: Tamil,
 * Malayalam, Oriya, Bengali, Punjabi and Gujarati are zero), and a tenant may
 * have none in any language. The app shows both rows unconditionally because it
 * cannot count without loading every meaning; the website gets the counts inside
 * the same 2KB it needs anyway, so it can decline to open a door into an empty
 * room. A failed load counts as zero and renders nothing — which is also what
 * makes this safe to deploy before the API that serves the artifact.
 */
export const MeaningsBrowseCards: React.FC = () => {
  const language = useSelector((state: RootState) => state.language.selectedLanguage);
  const languageCode = toLanguageCode(language);
  const [meanings, setMeanings] = useState<MeaningsIndex | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMeanings(null);
    fetchMeaningsIndex(languageCode)
      .then((idx) => { if (!cancelled) setMeanings(idx); })
      .catch(() => { /* no counts, no tiles */ });
    return () => { cancelled = true; };
  }, [languageCode]);

  const inCount = meanings?.inLanguage.size ?? 0;
  const otherCount = meanings?.otherLanguages.size ?? 0;

  /*
   * Native tints these blue and green; indigo and emerald are web's tokens for
   * those, the same mapping facets.ts documents. Both take the `book` glyph —
   * native uses an Octicon note, and a book is the nearest thing in this icon
   * set to "something to read".
   */
  return (
    <>
      {inCount > 0 && (
        <BrowseCard
          to={meaningScopeFilterHref(MEANING_SCOPE_IN)}
          title={`Meanings in ${languageNameForCode(languageCode)}`}
          subtitle={`${inCount} bhajan${inCount !== 1 ? 's' : ''} whose meaning is written in it`}
          icon={IoBook}
          accent="indigo"
        />
      )}
      {otherCount > 0 && (
        <BrowseCard
          to={meaningScopeFilterHref(MEANING_SCOPE_OTHER)}
          title="Other Language Meanings"
          subtitle={`${otherCount} bhajan${otherCount !== 1 ? 's' : ''} explained in another language`}
          icon={IoBook}
          accent="emerald"
        />
      )}
    </>
  );
};
